// Project → Feedback tab. Hits /projects/:id/feedback which aggregates
// client feedback across every presentation in the project, grouped by
// submission_id. Read-only. Submissions originate from the public
// presentation share link (submitClientFeedback).
//
// 2026-07-30 truth patch: the public page is comment-based, so this tab leads
// with the client's COMMENT text + count. Legacy thumb tallies only appear
// inline when a submission actually carries nonzero like/dislike data (old
// thumbs-era rows) — for today's comment-only traffic they'd read 0/0.
import { useQuery } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/utils";
import type { ProjectDetailResponse } from "@/pages/ProjectDetailPage";

interface ProjectFeedbackSubmission {
  submissionId: string;
  presentation: {
    id: string;
    presentationNumber: number;
  };
  submittedAt: string;
  viewerEmail: string | null;
  likesCount: number;
  dislikesCount: number;
  commentsCount: number;
  comments: string[];
}

interface ProjectFeedbackResponse {
  feedback: ProjectFeedbackSubmission[];
}

export function ProjectFeedbackTab() {
  const data = useOutletContext<ProjectDetailResponse>();
  const projectId = data.project.id;

  const { data: list, isLoading } = useQuery({
    queryKey: ["project", projectId, "feedback"],
    queryFn: () =>
      api<ProjectFeedbackResponse>(`/projects/${projectId}/feedback`),
  });

  const feedback = list?.feedback ?? [];

  const totalComments = feedback.reduce((sum, s) => sum + s.commentsCount, 0);

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Feedback
        </h2>
        <p className="text-xs text-brown-300">
          {feedback.length}{" "}
          {feedback.length === 1 ? "submission" : "submissions"}
          {feedback.length > 0 &&
            ` · 💬 ${totalComments} ${totalComments === 1 ? "comment" : "comments"}`}
        </p>
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-brown-100 bg-white p-10 text-center text-sm text-brown-300">
          Loading feedback…
        </div>
      ) : feedback.length === 0 ? (
        <div className="rounded-lg border border-brown-100 bg-white p-10 text-center text-sm text-brown-300">
          No feedback yet. Clients leave comments on any item from the public
          presentation share link.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-brown-100 bg-white">
          <table className="w-full">
            <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
              <tr>
                <th className="px-5 py-3">Presentation</th>
                <th className="px-5 py-3">Submitted</th>
                <th className="px-5 py-3">Viewer</th>
                <th className="px-5 py-3">Comments</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brown-50">
              {feedback.map((s) => {
                const hasThumbs = s.likesCount + s.dislikesCount > 0;
                return (
                  <tr key={s.submissionId} className="hover:bg-cream-50">
                    <td className="px-5 py-4 align-top">
                      <div className="font-semibold text-brown-500">
                        Presentation #{s.presentation.presentationNumber}
                      </div>
                    </td>
                    <td className="px-5 py-4 align-top text-sm text-brown-400">
                      {timeAgo(s.submittedAt)}
                    </td>
                    <td className="px-5 py-4 align-top text-sm text-brown-400">
                      {s.viewerEmail ?? "—"}
                    </td>
                    <td className="px-5 py-4 align-top text-sm text-brown-500">
                      <div className="flex items-center gap-2 font-semibold">
                        <span>
                          {s.commentsCount}{" "}
                          {s.commentsCount === 1 ? "comment" : "comments"}
                        </span>
                        {hasThumbs && (
                          <span className="text-xs font-normal text-brown-300">
                            · 👍 {s.likesCount} · 👎 {s.dislikesCount}
                          </span>
                        )}
                      </div>
                      {s.comments.length > 0 && (
                        <ul className="mt-2 space-y-1.5">
                          {s.comments.map((c, i) => (
                            <li
                              key={i}
                              className="whitespace-pre-wrap border-l-2 border-peach-300 pl-3 text-brown-400"
                            >
                              {c}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
